import { Slide } from '@mui/material';
import { getFlowSinkIds, getRAGBasePayload } from 'js/actions/rag';
import CodeContainer from 'js/components/data_sources/add_ds_form/IncomingWebhookConfiguration/CodeContainer';
import CopyButton from 'js/components/data_sources/add_ds_form/IncomingWebhookConfiguration/CopyButton';
import {
  getCurrentSelectedOption,
  SelectInput,
  SelectOption,
} from 'js/components/elements/rselect/ReactSelectUtils';
import SortedSelect from 'js/components/elements/rselect/SortedSelect';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { sanitizeApiKey } from 'js/lib/URLUtils';
import { useAPIUsage } from 'new/components/flows/types/genai_rag/context/APIUsage/useAPIUsage';
import useRagFlowId from 'new/components/flows/types/genai_rag/context/RagFlowId/useRagFlowId';
import { ReactComponent as CloseIcon } from 'new/components/icons/close-cross.svg';
import { IconButton } from 'new/elements/IconButton';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/Typography';
import useEntities from 'new/state/entities/repository/useEntities';
import { useVendors } from 'new/state/vendors/repository/useVendors';
import React, { useEffect, useMemo, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import classes from './RagActions.module.scss';

interface RagActionsProps {}

export const RAGAPIUsage = (props: RagActionsProps) => {
  const dispatch = useAppDispatch();
  const { llm: contextLLM, setLLM } = useAPIUsage();
  const [llm, setLocalLlm] = useState(contextLLM);
  const { data: flowNodes } = useEntities('flowNodes');
  const { data: sources } = useEntities('sources');
  const { data: sinks } = useEntities('sinks');
  const { data: apiKeys } = useEntities('apiKeys');
  const { vendors } = useVendors();
  const ragFlowId = useRagFlowId();
  const flow = flowNodes[ragFlowId];
  const source = sources[flow?.data_source_id];
  const { api_keys } = source || {};
  const { url, api_key } = apiKeys?.[api_keys?.[0]] || {};

  // find all the LLM sinks
  const llmSinks = getFlowSinkIds(flow, flowNodes).map((id) => sinks[id]) || [];
  const selectOptions = useMemo(() => {
    return llmSinks.map((sink) => {
      const vendor = vendors[sink.vendor_id] || {};
      const { template_config, data_credentials_id } = sink;
      const model = template_config[`${vendor.name}.rag_llm.model`];
      const provider = template_config[`${vendor.name}.rag_llm.provider`];
      const credential_id = String(data_credentials_id);
      return {
        value: sink.id,
        label: `${vendor.display_name} - ${model}`,
        description: `Credential ID: ${credential_id} - Model: ${model} - Provider: ${provider}`,
      };
    });
  }, [llmSinks]);
  const selectedOption = getCurrentSelectedOption(selectOptions, llm?.id);
  const sanitizedCURL = getCURL(true);
  const rawCURL = getCURL(false);

  useEffect(() => {
    // Adding this just because of the animation
    if (contextLLM) {
      setLocalLlm(contextLLM);
    } else {
      setTimeout(() => {
        setLocalLlm(contextLLM);
      }, 500);
    }
  }, [contextLLM]);

  function getCURL(sanitizeKey) {
    if (!ragFlowId || !llm || !url || !api_key) {
      return null;
    }

    const payload = dispatch(getRAGBasePayload('Your prompt goes here', llm.id));

    return `curl --location '${url}' \\
  --header "Authorization: Basic ${sanitizeKey ? sanitizeApiKey(api_key) : api_key}" \\
  --header "Content-Type: application/json" \\
  --data '${JSON.stringify(payload, null, 2)}'`;
  }

  const onClose = () => {
    setLLM(null);
  };

  const onChangeOption = ({ value }) => {
    setLLM(sinks[value]);
  };

  return (
    <Slide direction="up" in={!!contextLLM} unmountOnExit>
      <div className={classes.ragActionsContainer}>
        <Stack direction="row" alignItems="center" gap="36px" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={2}>
            <Stack>
              <Typography>API Configuration</Typography>
              <Typography variant="bodySmall" maxWidth="400px">
                <Typography variant="bodyDefault">
                  Use the configuration below to integrate RAG-powered AI queries into your
                  application. Select your desired LLM processor from the dropdown.
                </Typography>
              </Typography>
            </Stack>
            <Stack direction="column" justifyContent="space-between" alignItems="flex-end" gap={2}>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
              <SortedSelect
                styles={{
                  container: (base) => ({
                    ...base,
                    width: 240,
                  }),
                }}
                variant="default"
                components={{ Input: SelectInput, Option: SelectOption }}
                onChange={onChangeOption}
                value={selectedOption}
                options={selectOptions}
              />
            </Stack>
          </Stack>
        </Stack>
        {sanitizedCURL && (
          <SyntaxHighlighter language="bash" style={a11yDark}>
            {sanitizedCURL}
          </SyntaxHighlighter>
        )}
        {rawCURL && (
          <Stack justifyContent="flex-end" direction="row" alignItems="center" gap={2}>
            <CopyButton title="Copy cURL" toCopy={rawCURL} />
          </Stack>
        )}
      </div>
    </Slide>
  );
};
